import type { SecondFactorStrategy } from '../_hooks/use-sign-in-flow-store';

interface SignInWithSecondFactors {
  mfa: {
    sendEmailCode: () => Promise<unknown>;
  };
  supportedSecondFactors?: { strategy: string }[] | null;
}

/**
 * Picks the second-factor strategy to present for an in-progress sign-in,
 * preferring TOTP, then email code (which it also sends), then backup code.
 * Returns `null` when the account has no supported second factor.
 *
 * Shared by the SSO callback (which sets it before redirecting to the
 * verifications step) and the verifications screen's reload-restore path.
 */
export async function resolveSecondFactorStrategy(
  signIn: SignInWithSecondFactors,
): Promise<SecondFactorStrategy | null> {
  const supportedSecondFactors = signIn.supportedSecondFactors ?? [];

  if (supportedSecondFactors.some((factor) => factor.strategy === 'totp')) {
    return 'totp';
  }

  const emailCodeFactor = supportedSecondFactors.find((factor) => factor.strategy === 'email_code');
  if (emailCodeFactor) {
    await signIn.mfa.sendEmailCode();
    return 'email_code';
  }

  if (supportedSecondFactors.some((factor) => factor.strategy === 'backup_code')) {
    return 'backup_code';
  }

  return null;
}
